import { setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import AnonFeatureGuard from '@/components/guards/AnonFeatureGuard'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MenuHamburger, PaxtonWordmark } from '@/assets/icons'
import { AuthContext } from '@/context/auth-context'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { Link, useMatch, useParams } from 'react-router-dom'
import { useContext } from 'react'
import { NewChat } from '@/assets/icons'
import NewChatMatterBoundButton from '@/chat-common/new_chat/components/NewChatMatterBoundButton'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { IconButton } from '@/components/icon-button/IconButton'
import { useMatterOverview } from '@/matter/components/matter-overview/useMatterOverview'
import { RootState } from '@/store/store'
import { routePatterns, kRouteSegments } from '@/routing/paths'
import { canCreateChat } from '@/matter/utils/matter-permissions'
import TimelineAssetChatButton from '@/timeline/asset/components/TimelineAssetChatButton'
import TimelineAssetDownloadButton from '@/timeline/asset/components/TimelineAssetDownloadButton'
import { selectTimelineAssetForMatter } from '@/timeline/asset/store/selectors'
import MedChronAssetChatButton from '@/medical-chronology/asset/components/MedChronAssetChatButton'
import MedChronAssetDownloadButton from '@/medical-chronology/asset/components/MedChronAssetDownloadButton'
import { selectMedChronAssetForMatter } from '@/medical-chronology/asset/store/selectors'
import MedSummaryAssetChatButton from '@/medical-billing-summaries/asset/components/MedSummaryAssetChatButton'
import MedSummaryAssetDownloadButton from '@/medical-billing-summaries/asset/components/MedSummaryAssetDownloadButton'
import { selectMedSummaryAssetForMatter } from '@/medical-billing-summaries/asset/store/selectors'

/**
 * Dashboard Mobile Top Bar
 *
 * A mobile-only top bar which exposes UI controls to toggle components that are persistently visible
 * on desktop but hidden by default on small screens (e.g. the side nav).
 */
export default function DashboardMobileTopBar() {
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const { chatFeature, chatId } = useParams()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const { matter, handleNewChat } = useMatterOverview()
  const { logoUrl } = authContext
  const timelineRouteMatch = useMatch(routePatterns.timeline.matterFirst)
  const chronologyRouteMatch = useMatch(routePatterns.chronology.matterFirst)
  const billingRouteMatch = useMatch(routePatterns.billingSummaries.matterFirst)
  const timelineMatterId = timelineRouteMatch?.params.matterId
  const chronologyMatterId = chronologyRouteMatch?.params.matterId
  const billingMatterId = billingRouteMatch?.params.matterId

  const timelineAsset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId: timelineMatterId }))
  const medChronAsset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId: chronologyMatterId }))
  const medSummaryAsset = useAppSelector((state: RootState) => selectMedSummaryAssetForMatter(state, { matterId: billingMatterId }))

  const validChatFeature: boolean = chatFeature === kRouteSegments.assistant || chatFeature === kRouteSegments.agentV2
  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const showChatControls = validChatFeature && (!!chatId || mattersInitialLoadComplete)
  const displayTimelineAssetActions = timelineMatterId && timelineAsset && timelineAsset.total_events > 0
  const displayMedChronAssetActions = chronologyMatterId && medChronAsset && medChronAsset.total_events > 0
  // The breadcrumb bar hides its actions below the large breakpoint, so without this the summary
  // could be read on a phone but never downloaded.
  const displayMedSummaryAssetActions = billingMatterId && medSummaryAsset && medSummaryAsset.total_entries > 0
  const isMatterOverview = !!useMatch(routePatterns.matterOverview)

  return (
    <>
      <div className="h-[60px] w-full top-0 z-40 px-3 py-[10px] flex items-center bg-surface-content pr-3 border-b-[0.5px] border-border-default">
        {/* Mobile Sidebar Toggle */}
        <button
          type="button"
          className="flex items-center justify-center h-8 w-8 p-1 m-[4px] text-text-tertiary hover:bg-surface-hover-strong rounded-full"
          onClick={() => dispatch(setMobileSidebarOpen(true))}
        >
          <span className="sr-only">Open sidebar</span>
          <MenuHamburger className="h-6 w-6" aria-hidden="true" />
        </button>

        <div className="w-full flex items-center justify-between text-sm font-medium ml-3 h-10">
          <div className="flex gap-x-3 items-center">
            {/* Logo that links to the dashboard */}
            <div className="text-base font-extrabold tracking-tighter">
              <Link to={'/dashboard'}>
                {logoUrl === kDefaultLogoPath ? (
                  <PaxtonWordmark role="img" aria-label="Your legal AI assistant" className="h-8 w-[107px] shrink-0 text-brand-500" />
                ) : (
                  <img className="h-8" src={logoUrl} alt="Your legal AI assistant" />
                )}
              </Link>
            </div>
          </div>

          <div className="flex items-center">
            {showChatControls && (
              <AnonFeatureGuard showAuthOnGuard={false}>
                {/* New Chat Button — hidden while a draft is open, where starting a new chat isn't the primary action. */}
                {!draftArtifactVersionId && <NewChatMatterBoundButton iconOnly />}
              </AnonFeatureGuard>
            )}

            {isMatterOverview && (
              <IconButton
                aria-label="New chat"
                color="primary"
                disabled={!matter || !canCreateChat(matter)}
                icon={<NewChat />}
                onClick={handleNewChat}
                variant="plain"
                size="lg"
              />
            )}

            {displayTimelineAssetActions && (
              <div className="flex items-center justify-center">
                <TimelineAssetChatButton assetId={timelineAsset.id} matterId={timelineMatterId} compact />
                <TimelineAssetDownloadButton assetId={timelineAsset.id} compact />
              </div>
            )}

            {displayMedChronAssetActions && (
              <div className="flex items-center justify-center">
                <MedChronAssetChatButton assetId={medChronAsset.id} matterId={chronologyMatterId} compact />
                <MedChronAssetDownloadButton assetId={medChronAsset.id} compact />
              </div>
            )}

            {displayMedSummaryAssetActions && (
              <div className="flex items-center justify-center">
                <MedSummaryAssetChatButton assetId={medSummaryAsset.id} matterId={billingMatterId} compact />
                <MedSummaryAssetDownloadButton assetId={medSummaryAsset.id} compact />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
